import {
  boundsType,
  CategoryItem,
  City,
  CityItem,
  Option,
} from "@/types/mainTypes";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
// import { toast } from "sonner";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Extract a user-facing message from RTK Query / fetch errors. */
export function getApiErrorMessage(
  err: unknown,
  fallback = "Something went wrong"
): string {
  if (!err || typeof err !== "object") {
    return err instanceof Error ? err.message : fallback;
  }

  const error = err as {
    status?: number | string;
    data?: unknown;
    error?: string;
    message?: string;
  };

  const data = error.data;
  if (typeof data === "string" && data.trim()) return data;

  if (data && typeof data === "object") {
    const body = data as Record<string, unknown>;
    if (typeof body.error === "string" && body.error.trim()) return body.error;
    if (typeof body.message === "string" && body.message.trim())
      return body.message;
  }

  if (typeof error.error === "string" && error.error.trim()) return error.error;
  if (typeof error.message === "string" && error.message.trim())
    return error.message;

  if (error.status === "FETCH_ERROR") {
    return "Network error — check your connection and try again";
  }

  return fallback;
}

/** Skip Next.js optimizer for hosts that fail /_next/image (e.g. ispot.cc). */
export function shouldUnoptimizeImage(src: string): boolean {
  if (!src || src.startsWith("/")) return false;
  try {
    const { hostname } = new URL(src);
    return hostname === "st79068.ispot.cc" || hostname.endsWith(".ispot.cc");
  } catch {
    return false;
  }
}


// --- Types ---

export const allCategories: CategoryItem[] = [
  { id: 1, title: "Commercial" },
  { id: 2, title: "Residential" },
  { id: 3, title: "Company" },
  { id: 4, title: "Project" },
  { id: 5, title: "Goods" },
  { id: 6, title: "Factory" },
  { id: 7, title: "Stock" },
  { id: 8, title: "Brands" },
  { id: 9, title: "Service" },
  { id: 10, title: "Living" },
  { id: 11, title: "Office" },
  { id: 12, title: "Market" },
  { id: 13, title: "Service Building" },
  { id: 14, title: "Service Villa" },
  { id: 15, title: "Land (Service)" },
  { id: 16, title: "Store" },
  { id: 17, title: "Apartment" },
  { id: 18, title: "Villa" },
  { id: 19, title: "Land (Living)" },
  { id: 20, title: "Building" },
  { id: 21, title: "Chalet" },
  { id: 22, title: "Farm" },
  { id: 23, title: "Manors" },
];

export const typeOptions: Option[] = [
  { id: 1, title: "rent" },
  { id: 2, title: "sell" },
];

export const cityItems: CityItem[] = [
  { id: 1, title: "Doha" },
  { id: 2, title: "Al Rayyan" },
  { id: 3, title: "Umm Salal" },
  { id: 4, title: "Al Khawr wa adh Dhakhira" },
  { id: 5, title: "Al Wakrah" },
  { id: 6, title: "Al Daayen" },
  { id: 7, title: "Al Shamal" },
  { id: 8, title: "Al Shahaniyah" },
];

export const conditions: Option[] = [
  { id: 1, title: "new" },
  { id: 2, title: "used" },
  { id: 3, title: "old" },
];

const categoryTitleMap: Record<number, string> = allCategories.reduce(
  (acc, { id, title }) => {
    acc[id] = title;
    return acc;
  },
  {} as Record<number, string>
);

const categoryIdMapByTitle: Record<string, number> = allCategories.reduce(
  (acc, { id, title }) => {
    acc[title] = id;
    return acc;
  },
  {} as Record<string, number>
);

// Create lookup maps for types
const typeTitleMap: Record<number, string> = typeOptions.reduce(
  (acc, { id, title }) => {
    acc[id] = title;
    return acc;
  },
  {} as Record<number, string>
);

const typeIdMapByTitle: Record<string, number> = typeOptions.reduce(
  (acc, { id, title }) => {
    acc[title] = id;
    return acc;
  },
  {} as Record<string, number>
);

// Create lookup maps for cities
const cityTitleMap: Record<number, string> = cityItems.reduce(
  (acc, { id, title }) => {
    acc[id] = title;
    return acc;
  },
  {} as Record<number, string>
);

const cityIdMapByTitle: Record<string, number> = cityItems.reduce(
  (acc, { id, title }) => {
    acc[title] = id;
    return acc;
  },
  {} as Record<string, number>
);

// Create lookup maps for conditions
const conditionTitleMap: Record<number, string> = conditions.reduce(
  (acc, { id, title }) => {
    acc[id] = title;
    return acc;
  },
  {} as Record<number, string>
);

const conditionIdMapByTitle: Record<string, number> = conditions.reduce(
  (acc, { id, title }) => {
    acc[title] = id;
    return acc;
  },
  {} as Record<string, number>
);

// Exported utility functions
export function getCategTitleById(id: number): string | undefined {
  return categoryTitleMap[id];
}

export function getCategIdByTitle(title: string): number | undefined {
  console.log("Title:", title);
  console.log("Category ID Map By Title:", categoryIdMapByTitle[title]);
  return categoryIdMapByTitle[title];
}

export function getTypeTitleById(id: number): string | undefined | null {
  if (!id || !typeTitleMap[id]) {
    return null;
  }
  return typeTitleMap[id];
}

export function getTypeIdByTitle(title: string): number | undefined {
  return typeIdMapByTitle[title];
}

export function getCityTitleById(id: number): string | undefined {
  return cityTitleMap[id];
}

export function getCityIdByTitle(title: string): number | undefined {
  return cityIdMapByTitle[title];
}

export function getConditionTitleById(id: number): string | undefined {
  return conditionTitleMap[id];
}

export function getConditionIdByTitle(title: string): number | undefined {
  return conditionIdMapByTitle[title];
}

export function GetDateTime(dateString: string): string {
  const getDate = dateString.split("T")[0];
  return getDate;
}
export function isCityInView(city: City, bounds: boundsType) {
  return (
    city.coordinates.lat <= bounds.north &&
    city.coordinates.lat >= bounds.south &&
    city.coordinates.lng >= bounds.west &&
    city.coordinates.lng <= bounds.east
  );
}
